<template>
  <div>
    <b-card>
      <TitleTable titleTable="Distributors" iconTable="fas fa-truck" />
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="d-flex justify-content-end align-items-center pt-2 border-top">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openAdd">Add distributor</b-button>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No distributors found"
        class="wl-distributors-table">
        <template #cell(contact_name)="data">
          {{ displayValue(data.value) }}
        </template>
        <template #cell(phone)="data">
          {{ displayValue(data.value) }}
        </template>
        <template #cell(email)="data">
          {{ displayValue(data.value) }}
        </template>
        <template #cell(address)="data">
          <span
            v-if="data.value"
            v-b-tooltip.hover
            :title="data.value"
            class="wl-cell-truncate">
            {{ truncate(data.value, 36) }}
          </span>
          <span v-else class="text-muted font-italic">—</span>
        </template>
        <template #cell(notes)="data">
          <span
            v-if="data.value"
            v-b-tooltip.hover
            :title="data.value"
            class="wl-cell-truncate">
            {{ truncate(data.value, 28) }}
          </span>
          <span v-else class="text-muted font-italic">—</span>
        </template>
        <template #cell(active)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="remove(data.item)">
            <i class="fas fa-trash-alt"></i>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <WlFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="inv-dist-sidebar"
      :title="editId ? 'Edit distributor' : 'New distributor'"
      :loading="saving"
      @submit="onSubmit"
      @close="onSidebarClose">
      <b-alert
          v-if="showValidationErrors && validationSummary"
          show
          variant="danger"
          class="py-2 small mb-2">
          <strong>Required:</strong> {{ validationSummary }}
        </b-alert>

      <b-form-group label-for="dist-name">
        <template #label>
          Name <span class="text-danger">*</span>
        </template>
        <b-form-input
          id="dist-name"
          v-model="form.name"
          :state="fieldState('name')"
          trim
          @input="clearFieldError('name')" />
        <b-form-invalid-feedback v-if="fieldErrors.name">
          {{ fieldErrors.name }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-for="dist-contact">
        <template #label>
          Contact name <span class="text-danger">*</span>
        </template>
        <b-form-input
          id="dist-contact"
          v-model="form.contact_name"
          :state="fieldState('contact_name')"
          trim
          @input="clearFieldError('contact_name')" />
        <b-form-invalid-feedback v-if="fieldErrors.contact_name">
          {{ fieldErrors.contact_name }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-for="dist-phone">
        <template #label>
          Phone <span class="text-danger">*</span>
        </template>
        <b-form-input
          id="dist-phone"
          v-model="form.phone"
          :state="fieldState('phone')"
          trim
          @input="clearFieldError('phone')" />
        <b-form-invalid-feedback v-if="fieldErrors.phone">
          {{ fieldErrors.phone }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-for="dist-email">
        <template #label>
          Email <span class="text-danger">*</span>
        </template>
        <b-form-input
          id="dist-email"
          v-model="form.email"
          type="email"
          :state="fieldState('email')"
          trim
          autocomplete="off"
          @input="clearFieldError('email')" />
        <b-form-invalid-feedback v-if="fieldErrors.email">
          {{ fieldErrors.email }}
        </b-form-invalid-feedback>
      </b-form-group>

        <b-form-group label="Address" label-for="dist-address">
          <b-form-input
            id="dist-address"
            v-model="form.address"
            trim
            placeholder="Optional — street, city, state" />
        </b-form-group>

        <b-form-group label="Notes" label-for="dist-notes">
          <b-form-textarea
            id="dist-notes"
            v-model="form.notes"
            rows="2"
            placeholder="Optional" />
        </b-form-group>

        <b-form-group label="Active">
          <b-form-checkbox v-model="form.active" switch />
        </b-form-group>
    </WlFormSidebar>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BBadge,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BAlert,
  VBTooltip,
} from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import WlFormSidebar from '../components/WlFormSidebar.vue'
import invDistributorsApi from '@/core/services/api/weightloss-inventory/distributors'
import wlAdminMixin from '../mixins/wlAdminMixin'
import Swal from 'sweetalert2'

function emptyForm() {
  return {
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    active: true,
  }
}

export default {
  name: 'TableInvDistributors',
  mixins: [wlAdminMixin],
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BAlert,
    TitleTable,
    GoBack,
    ToastNotification,
    WlFormSidebar,
  },
  data() {
    return {
      fields: [
        { key: 'id', label: 'ID', thStyle: { width: '4%' } },
        { key: 'name', label: 'Name' },
        { key: 'contact_name', label: 'Contact' },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
        { key: 'address', label: 'Address' },
        { key: 'notes', label: 'Notes' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      sidebarVisible: false,
      saving: false,
      editId: null,
      form: emptyForm(),
      showValidationErrors: false,
      validationSummary: '',
      fieldErrors: {},
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    fieldState(key) {
      if (!this.fieldErrors[key]) return null
      return false
    },
    clearFieldError(key) {
      if (this.fieldErrors[key]) {
        const next = { ...this.fieldErrors }
        delete next[key]
        this.fieldErrors = next
      }
    },
    resetFieldErrors() {
      this.fieldErrors = {}
      this.showValidationErrors = false
      this.validationSummary = ''
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    },
    validateForm() {
      const errors = {}
      const name = String(this.form.name || '').trim()
      const contact = String(this.form.contact_name || '').trim()
      const phone = String(this.form.phone || '').trim()
      const email = String(this.form.email || '').trim()

      if (!name) errors.name = 'Name is required.'
      if (!contact) errors.contact_name = 'Contact name is required.'
      if (!phone) errors.phone = 'Phone is required.'
      else if (phone.length > 20) errors.phone = 'Phone must be at most 20 characters.'
      if (!email) errors.email = 'Email is required.'
      else if (!this.isValidEmail(email)) errors.email = 'Enter a valid email address.'

      return errors
    },
    validationSummaryFromErrors(errors) {
      const labels = {
        name: 'Name',
        contact_name: 'Contact name',
        phone: 'Phone',
        email: 'Email',
      }
      return Object.keys(errors)
        .map(key => labels[key] || key)
        .join(', ')
    },
    mapItemToForm(item) {
      return {
        name: item.name != null ? String(item.name) : '',
        contact_name: item.contact_name != null ? String(item.contact_name) : '',
        phone: item.phone != null ? String(item.phone) : '',
        email: item.email != null ? String(item.email) : '',
        address: item.address != null ? String(item.address) : '',
        notes: item.notes != null ? String(item.notes) : '',
        active: item.active !== false && item.active !== 0,
      }
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
    truncate(text, max) {
      const s = String(text)
      return s.length > max ? `${s.slice(0, max)}...` : s
    },
    onSidebarClose() {
      this.editId = null
      this.resetFieldErrors()
    },
    async load() {
      this.loading = true
      try {
        this.items = await invDistributorsApi.list()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.editId = null
      this.form = emptyForm()
      this.resetFieldErrors()
      this.sidebarVisible = true
    },
    openEdit(item) {
      this.editId = item.id
      this.form = this.mapItemToForm(item)
      this.resetFieldErrors()
      this.sidebarVisible = true
    },
    onSubmit() {
      const errors = this.validateForm()
      const errorKeys = Object.keys(errors)

      if (errorKeys.length) {
        this.fieldErrors = errors
        this.showValidationErrors = true
        this.validationSummary = this.validationSummaryFromErrors(errors)
        this.$refs.toast.danger(
          `Please complete the required fields: ${this.validationSummary}.`
        )
        return
      }

      this.resetFieldErrors()
      this.save()
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          name: String(this.form.name).trim(),
          contact_name: String(this.form.contact_name).trim(),
          phone: String(this.form.phone).trim(),
          email: String(this.form.email).trim(),
          active: !!this.form.active,
        }
        payload.address = String(this.form.address || '').trim()
        payload.notes = String(this.form.notes || '').trim()
        if (this.editId) {
          await invDistributorsApi.update(this.editId, payload)
          this.$refs.toast.success('Distributor updated')
        } else {
          await invDistributorsApi.create(payload)
          this.$refs.toast.success('Distributor created')
        }
        this.sidebarVisible = false
        this.editId = null
        this.load()
      } catch (e) {
        this.handleApiError(e, this.$refs.toast)
      } finally {
        this.saving = false
      }
    },
    remove(item) {
      Swal.fire({
        title: 'Delete distributor?',
        text: item.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      }).then(async r => {
        if (r.value) {
          try {
            await invDistributorsApi.remove(item.id)
            this.$refs.toast.success('Distributor deleted')
            this.load()
          } catch (e) {
            this.$refs.toast.danger(e.message)
          }
        }
      })
    },
  },
}
</script>

<style scoped>
.wl-cell-truncate {
  display: inline-block;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
